import React from 'react';
import styled from 'styled-components';
import useLocale from '../../hooks/useLocale';
import HanumanSpecialDayBanner from './HanumanSpecialDayBanner';
import { HANUMAN_COLORS } from './hanumanColors';

const Card = styled.article`
  max-width: 640px;
  margin: 0 auto;
  padding: ${p => p.theme.spacing[5]}px;
  background: linear-gradient(180deg, #FFFBF0, #FFE0B2);
  border: 1px solid ${HANUMAN_COLORS.cellBorder};
  border-radius: 16px;
  box-shadow: 0 6px 24px rgba(198,40,40,0.08);
  text-align: center;

  @media (max-width: ${p => p.theme.breakpoints.tablet}px) {
    padding: ${p => p.theme.spacing[4]}px;
  }
`;

const Motif = styled.div`
  font-size: 36px;
  margin-bottom: ${p => p.theme.spacing[2]}px;
  line-height: 1;
`;

const Heading = styled.h2`
  font-family: 'Noto Sans Tamil', 'Playfair Display', serif;
  font-size: 22px;
  color: ${HANUMAN_COLORS.primaryDark};
  margin: 0 0 ${p => p.theme.spacing[4]}px;
`;

const CollectedStrip = styled.div`
  display: flex;
  justify-content: center;
  gap: 6px;
  margin: ${p => p.theme.spacing[4]}px 0;
  flex-wrap: wrap;
`;

const CollectedLetter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 36px;
`;

const Dev = styled.span`
  font-family: 'Noto Sans Devanagari', serif;
  font-size: 26px;
  font-weight: 600;
  color: ${HANUMAN_COLORS.textPrimary};

  @media (max-width: ${p => p.theme.breakpoints.mobile}px) {
    font-size: 20px;
  }
`;

const Ta = styled.span`
  font-family: 'Noto Sans Tamil', sans-serif;
  font-size: 11px;
  color: ${HANUMAN_COLORS.textSecondary};
`;

const VerseDev = styled.p`
  font-family: 'Noto Sans Devanagari', serif;
  font-size: 18px;
  line-height: 1.8;
  color: #2C1810;
  margin: ${p => p.theme.spacing[4]}px 0 ${p => p.theme.spacing[2]}px;
`;

const VerseTa = styled.p`
  font-family: 'Noto Sans Tamil', sans-serif;
  font-size: 15px;
  line-height: 1.7;
  color: ${p => p.theme.colors.text.secondary};
  margin: 0 0 ${p => p.theme.spacing[2]}px;
`;

const Attribution = styled.p`
  font-size: 12px;
  font-style: italic;
  color: ${HANUMAN_COLORS.primary};
  margin: 0 0 ${p => p.theme.spacing[4]}px;
`;

const InterpHeading = styled.h3`
  font-family: 'Noto Sans Tamil', 'Playfair Display', serif;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: ${HANUMAN_COLORS.primaryDark};
  margin: ${p => p.theme.spacing[5]}px 0 ${p => p.theme.spacing[2]}px;
`;

const InterpPrimary = styled.p`
  font-family: 'Noto Sans Tamil', sans-serif;
  font-size: 16px;
  line-height: 1.7;
  color: #2C1810;
  margin: 0 0 ${p => p.theme.spacing[3]}px;
`;

const InterpSecondary = styled.p`
  font-family: ${p => p.theme.typography.fontFamily};
  font-size: 14px;
  line-height: 1.6;
  color: ${p => p.theme.colors.text.secondary};
  font-style: italic;
  margin: 0;
`;

const Actions = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing[3]}px;
  justify-content: center;
  margin-top: ${p => p.theme.spacing[5]}px;

  @media (max-width: ${p => p.theme.breakpoints.mobile}px) {
    flex-direction: column;
  }
`;

const ShareBtn = styled.button`
  padding: 12px 32px;
  background: linear-gradient(135deg, ${HANUMAN_COLORS.primary}, ${HANUMAN_COLORS.accent});
  color: #fff;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  min-height: 44px;
  font-family: 'Noto Sans Tamil', ${p => p.theme.typography.fontFamily};
`;

const ReturnBtn = styled.button`
  padding: 12px 32px;
  background: none;
  border: 1px solid ${HANUMAN_COLORS.primary};
  color: ${HANUMAN_COLORS.primaryDark};
  border-radius: 8px;
  cursor: pointer;
  min-height: 44px;
  font-family: 'Noto Sans Tamil', ${p => p.theme.typography.fontFamily};
`;

const HanumanResultCard = ({
  pathCells, collectedLetters, collectedLettersTa,
  verse, verse_ta, chalisaLineNumber,
  interpretation_ta, interpretation_en,
  onShare, onReturn, lang,
  grid, gridTa,
}) => {
  const t = useLocale(lang, 'hanumanPrashnavali');
  const primary = lang === 'ta' ? interpretation_ta : interpretation_en;
  const secondary = lang === 'ta' ? interpretation_en : interpretation_ta;
  const attribution = (t.chalisaLineAttribution || 'Hanuman Chalisa — line {n}').replace('{n}', chalisaLineNumber);

  return (
    <Card>
      <HanumanSpecialDayBanner variant="result" lang={lang} />
      <Motif aria-hidden="true">🙏</Motif>
      <Heading>{t.resultHeading || "Hanuman's Counsel"}</Heading>

      <CollectedStrip aria-label={t.collectedLettersAria || 'Collected letters from the path'}>
        {pathCells.map((idx, k) => (
          <CollectedLetter key={idx}>
            <Dev>{grid?.[idx] || collectedLetters[k]}</Dev>
            <Ta>{gridTa?.[idx] || ''}</Ta>
          </CollectedLetter>
        ))}
      </CollectedStrip>

      <VerseDev>{verse}</VerseDev>
      <VerseTa>{verse_ta}</VerseTa>
      <Attribution>{attribution}</Attribution>

      <InterpHeading>{t.interpretationHeading || 'Counsel'}</InterpHeading>
      <InterpPrimary>{primary}</InterpPrimary>
      <InterpSecondary>{secondary}</InterpSecondary>

      <Actions>
        <ShareBtn onClick={onShare}>{t.shareButton || 'Share'}</ShareBtn>
        <ReturnBtn onClick={onReturn}>{t.returnButton || 'Return'}</ReturnBtn>
      </Actions>
    </Card>
  );
};

export default HanumanResultCard;
